import { Component, Input } from '@angular/core';
import { OrderDetailModel, PipelineObject } from '@cv-invision/cv.invision-intelligence.ux';
import { TelemetryEventService } from '@cv-invision/core';
import { OverviewSubHeaderService } from '../service/overview-sub-header.service';
import { DialogService } from 'primeng/dynamicdialog';
import { PipelineDialogComponent } from '../pipeline-dialog/pipeline-dialog.component';
import { PipelineStatusStyle } from './model/order-info.model';

@Component({
	selector: 'cv-order-info',
	templateUrl: './order-info.component.html',
	styleUrls: ['./order-info.component.scss'],
	host: {
		class: 'd-flex align-items-center justify-content-between flex-grow-1 gap-3'
	}
})
export class OrderInfoComponent {
	@Input() orderInboxModel = new OrderDetailModel();
	@Input() orderUsers: any[] = [];

	readonly guaranteeStatusMap: Record<number, { colorClass: string; icon: string }> = {
		1: { colorClass: 'ci-order-info__status-icon--pending', icon: 'fa-hourglass' },
		2: { colorClass: 'ci-order-info__status-icon--eligible', icon: 'fa-shield-check' },
		3: { colorClass: 'ci-order-info__status-icon--ineligible', icon: 'fa-shield-xmark' }
	};

	private readonly statusPipelineStyles: Record<string, PipelineStatusStyle> = {
		subsequentreport: {
			colorClass: 'ci-order-info__status-icon--subsequent',
			icon: 'fa-file-circle-check',
			tooltip: 'Subsequent Report'
		},
		dualreport: {
			colorClass: 'ci-order-info__status-icon--dual',
			icon: 'fa-layer-group',
			tooltip: 'Dual Report'
		}
	};

	get showStatusIcons(): boolean {
		return (
			!!this.guaranteeStatusMap[this.orderInboxModel?.guaranteeState ?? 0] ||
			!!this.orderInboxModel?.revised ||
			this.statusPipelineButtons.length > 0
		);
	}

	get statusPipelineButtons(): Array<{ indicator: PipelineObject; style: PipelineStatusStyle }> {
		return this.pipelineIndicators
			.map((indicator) => {
				const style = this.getPipelineStatusStyle(indicator);
				return style ? { indicator, style } : null;
			})
			.filter((item): item is { indicator: PipelineObject; style: PipelineStatusStyle } => item !== null)
			.sort((a, b) => this.getPipelineStatusOrder(a.indicator) - this.getPipelineStatusOrder(b.indicator));
	}

	private get pipelineIndicators(): PipelineObject[] {
		return this.orderInboxModel.pipelineIndicators ?? [];
	}

	constructor(
		private telemetryEventService: TelemetryEventService,
		private overViewSubheaderService: OverviewSubHeaderService,
		private dialogService: DialogService
	) {}

	/**
	 * return to the main intelligence module but sending the telemetry event
	 */
	returnBackClicked(): void {
		this.telemetryEventService.setEvent('review-stop', 'User.GoBackInbox');
		this.overViewSubheaderService.subheaderEvents.next({
			name: 'RETURN_HOME',
			event: null
		});
	}

	/**
	 * Triggers dialog when a pipeline element is clicked
	 * @param indicator
	 */
	handlePipelineClick(indicator: PipelineObject): void {
		const title = `Pipeline Indicator: ${indicator.label}`;
		const message = indicator.message;

		this.dialogService.open(PipelineDialogComponent, {
			header: title,
			width: '600px',
			data: {
				content: message
			}
		});
	}

	/**
	 * Status-icon style for Dual Report and Subsequent Report
	 * @param indicator
	 */
	getPipelineStatusStyle(indicator: PipelineObject): PipelineStatusStyle | undefined {
		return (
			this.statusPipelineStyles[this.normalizePipelineId(indicator.key)] ||
			this.statusPipelineStyles[this.normalizePipelineId(indicator.label)]
		);
	}

	/**
	 * Track the pipeline button
	 * @param _index
	 * @param item
	 * @returns
	 */
	trackPipelineButton(_index: number, item: { indicator: PipelineObject }): string {
		return item.indicator.key || item.indicator.label;
	}

	/**
	 * Normalize the pipeline id
	 * @param value
	 * @returns
	 */
	private normalizePipelineId(value: string): string {
		return (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
	}

	/**
	 * Get the pipeline status order
	 * @param indicator
	 * @returns
	 */
	private getPipelineStatusOrder(indicator: PipelineObject): number {
		const id = this.normalizePipelineId(indicator.label || indicator.key);
		if (id.includes('subsequent')) {
			return 0;
		}
		if (id.includes('dual')) {
			return 1;
		}
		return 2;
	}
}
